import React from 'react';
import { StandardTableActions, Badge, ColoredBadge } from '../../components/ui';

import { Column } from '../../components/DataTable/DataTable';
import { TruncatedText } from '../../components/DataTable/TruncatedText';
import { Task } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { APP_CONFIG, formatDate as formatDateGlobal, formatDateTime as formatDateTimeGlobal } from '../../config/appConfig';
import { ThemeColors } from '../../contexts/ThemeContext';
import LinkedConversationsSection from '../conversations/components/LinkedConversationsSection';
import ActivityLogSection from '../activity-logs/components/ActivityLogSection';
import { TaskAttachmentsSection } from './components/TaskAttachmentsSection';
import { normalizeHexColor, parseStatusColorConfig } from '../../utils/statusColor';

const getTaskStatusBadgeVariant = (
  status: string,
): 'default' | 'secondary' | 'accent' | 'danger' | 'warning' | 'info' => {
  const statusLower = status.toLowerCase();
  if (statusLower.includes('not started') || statusLower.includes('red')) {
    return 'danger';
  }
  if (statusLower.includes('in progress') || statusLower.includes('yellow')) {
    return 'warning';
  }
  if (statusLower.includes('awaiting') || statusLower.includes('blue')) {
    return 'info';
  }
  if (statusLower.includes('completed') || statusLower.includes('green')) {
    return 'accent';
  }
  return 'secondary';
};

const renderTaskStatusBadge = (
  status?: string,
  color?: string,
  className?: string,
) => {
  if (!status) {
    return null;
  }

  const normalizedColor = normalizeHexColor(parseStatusColorConfig(color).backgroundColor);
  if (normalizedColor) {
    return <ColoredBadge label={status} color={normalizedColor} className={className} />;
  }

  return <Badge variant={getTaskStatusBadgeVariant(status)} className={className}>{status}</Badge>;
};

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Tasks',
    description: 'Manage your tasks database',
    permissionModuleKey: 'crm_tasks',
    route: {
      list: '/crm_tasks',
      new: '/crm_tasks/new',
      detail: (id: number) => `/crm_tasks/${id}`,
      edit: (id: number) => `/crm_tasks/${id}/edit`,
    },
    // API Endpoints
    api: {
      list: '/crm-tasks',
      detail: (id: number) => `/crm-tasks/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search tasks by description, client, or assigned user...',
  },

  // Filter Groups Configuration
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'taskDescription', 'clientId', 'taskAssignedTo', 'taskStatus', 'taskDate', 'taskCreatedAt',
        'taskStartDate', 'taskEndDate', 'taskEstimatedDate'
      ],
    },
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'taskDescription',
        label: 'Description',
        placeholder: 'All Description',
        type: 'text' as const,
        getValue: (item: Task) => item.taskDescription,
        options: [],
      },
      {
        key: 'clientId',
        label: 'Client',
        placeholder: 'All Clients',
        type: 'select' as const,
        getValue: (item: Task) => item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : ''),
        options: [],
      },
      {
        key: 'taskAssignedTo',
        label: 'Asgn',
        placeholder: 'All Users',
        type: 'select' as const,
        getValue: (item: Task) => 
          item.taskAssignedToUser
            ? (item.taskAssignedToUser.userDisplayName || item.taskAssignedToUser.userFirstName || `User #${item.taskAssignedToUser.id}`)
            : (item.taskAssignedTo ? `User #${item.taskAssignedTo}` : ''),
        options: [],
      },
      {
        key: 'taskStatus',
        label: 'Status',
        placeholder: 'All Status',
        type: 'select' as const,
        getValue: (item: Task) => item.taskStatusDescr?.ts_descr || '',
        options: [],
      },
      {
        key: 'taskDate',
        label: 'Task Date',
        placeholder: 'All Task Date',
        type: 'date' as const,
        getValue: (item: Task) =>
          item.taskDate
            ? new Date(item.taskDate).toLocaleDateString()
            : undefined,
        options: [],
      },
      {
        key: 'taskStartDate',
        label: 'Start Date',
        placeholder: 'All Start Date',
        type: 'date' as const,
        getValue: (item: Task) =>
          item.taskStartDate ? formatDateTimeGlobal(item.taskStartDate) : undefined,
        options: [],
      },
      {
        key: 'taskEndDate',
        label: 'End Date',
        placeholder: 'All End Date',
        type: 'date' as const,
        getValue: (item: Task) =>
          item.taskEndDate ? formatDateTimeGlobal(item.taskEndDate) : undefined,
        options: [],
      },
      {
        key: 'taskCreatedAt',
        label: 'Created At',
        placeholder: 'All Created At',
        type: 'date' as const,
        getValue: (item: Task) =>
          item.taskCreatedAt
            ? new Date(item.taskCreatedAt).toLocaleDateString()
            : undefined,
        options: [],
      },
    ],
    // Filter logic functions
    applyFilter: (
      items: Task[],
      filters: {
        taskDescription: string[] | string;
        clientId: string[];
        taskAssignedTo: string[];
        taskStatus: string[];
        taskDate: string[];
        taskStartDate: string[];
        taskEndDate: string[];
        taskCreatedAt: string[];
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // Description filter
        const descrFilter = filters.taskDescription;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.taskDescription || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.taskDescription || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Client filter (matches by display name or ID)
        if (filters.clientId && filters.clientId.length > 0) {
          const displayValue = item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : '');
          const matches = filters.clientId.some((filter) => {
            return displayValue === filter || 
                   (item.clientId && filter === `Client #${item.clientId}`) ||
                   (item.client?.clientName && filter === item.client.clientName);
          });
          if (!matches) return false;
        }

        // Asgn filter (matches by display name or ID)
        if (filters.taskAssignedTo && filters.taskAssignedTo.length > 0) {
          const displayValue = item.taskAssignedToUser
            ? (item.taskAssignedToUser.userDisplayName || item.taskAssignedToUser.userFirstName || `User #${item.taskAssignedToUser.id}`)
            : (item.taskAssignedTo ? `User #${item.taskAssignedTo}` : '');
          const matches = filters.taskAssignedTo.some((filter) => {
            return displayValue === filter ||
                   (item.taskAssignedTo && filter === `User #${item.taskAssignedTo}`) ||
                   (item.taskAssignedToUser?.userDisplayName && filter === item.taskAssignedToUser.userDisplayName) ||
                   (item.taskAssignedToUser?.userFirstName && filter === item.taskAssignedToUser.userFirstName) ||
                   (item.taskAssignedToUser?.id && filter === `User #${item.taskAssignedToUser.id}`);
          });
          if (!matches) return false;
        }

        // Status filter (matches by description)
        if (filters.taskStatus && filters.taskStatus.length > 0) {
          const displayValue = item.taskStatusDescr?.ts_descr || '';
          const matches = filters.taskStatus.some((filter) => {
            return displayValue === filter ||
                   displayValue.toLowerCase().includes(filter.toLowerCase()) || 
                   filter.toLowerCase().includes(displayValue.toLowerCase());
          });
          if (!matches) return false;
        }

        // Task Date filter
        if (filters.taskDate.length > 0) {
          if (ranges.taskDate) {
            const range = ranges.taskDate;
            if (range.start && range.end && item.taskDate) {
              const itemDate = new Date(item.taskDate);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.taskDate
              ? new Date(item.taskDate).toLocaleDateString()
              : '';
            const matches = filters.taskDate.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Start Date filter
        if (filters.taskStartDate.length > 0) {
          if (ranges.taskStartDate) {
            const range = ranges.taskStartDate;
            if (range.start && range.end && item.taskStartDate) {
              const itemDate = new Date(item.taskStartDate);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.taskStartDate ? formatDateTimeGlobal(item.taskStartDate) : '';
            const matches = filters.taskStartDate.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // End Date filter
        if (filters.taskEndDate.length > 0) {
          if (ranges.taskEndDate) {
            const range = ranges.taskEndDate;
            if (range.start && range.end && item.taskEndDate) {
              const itemDate = new Date(item.taskEndDate);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.taskEndDate ? formatDateTimeGlobal(item.taskEndDate) : '';
            const matches = filters.taskEndDate.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Created At filter
        if (filters.taskCreatedAt.length > 0) {
          if (ranges.taskCreatedAt) {
            const range = ranges.taskCreatedAt;
            if (range.start && range.end && item.taskCreatedAt) {
              const itemDate = new Date(item.taskCreatedAt);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.taskCreatedAt ? formatDateTimeGlobal(item.taskCreatedAt) : '';
            const matches = filters.taskCreatedAt.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        return true;
      });
    },
  },

  // Toolbar Actions Configuration (view toggle added in List.tsx for same style as Notes)
  toolbar: {
    actions: [
      {
        key: 'monthly-view',
        icon: 'calendar',
        title: 'Tasks Monthly View',
        showTitle: false,
        variant: 'default' as const,
        position: 'beforeTablePresets' as const,
        href: '/crm_tasks-monthly',
      },
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }

              try {
                const { exportToExcel } = await import('../../components/exports');
                await exportToExcel(exportData);
              } catch (error: any) {
                console.error('Failed to export to Excel:', error);
                throw new Error(error?.message || 'Failed to export to Excel. Please check the console for details.');
              }
            },
          },
        ],
      },
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Task',
        showTitle: false,
        variant: 'primary' as const,
        href: '/crm_tasks/new',
      },
    ],
  },

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: Task) => {
      window.location.href = `/crm_tasks/${item.taskId}`;
    },
    keyExtractor: (item: Task) => item.taskId,
    getClientId: (item: Task) => item.clientId ?? null,
  },

  // Empty States
  emptyStates: {
    noData: 'Get started by creating a new task.',
    noSearchResults: 'No tasks found matching your search criteria.',
    noFilterResults: 'No tasks match the selected filters.',
  },

  // Delete Confirmation
  delete: {
    title: 'Delete Task',
    message: 'Are you sure you want to delete this task? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete task. Please try again.',
    handler: async (id: number) => {
      const { deleteTask } = await import('../../services/mutations');
      await deleteTask(id);
    },
  },

  // Utility Functions
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString) || '-';
    },
    formatDateTime: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateTimeGlobal(dateString) || '-';
    },
  },

  // Detail View Configuration
  detail: {
    subtitle: 'Task Details',
    notFoundTitle: 'Task not found',
    notFoundMessage: 'The task you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Tasks',
    defaultTitle: 'Unnamed Task',
    getTitle: (item: Task) => {
      return item.taskDescription || `Task #${item.taskId}`;
    },
  },

  // Form Configuration
  form: {
    new: {
      title: 'New Task',
      subtitle: 'Add a new task to your database',
      submitText: 'Create Task',
    },
    edit: {
      title: 'Edit Task',
      submitText: 'Update Task',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading task...',
    errorMessage: 'Failed to save task. Please try again.',
    supportsFiles: true,
    create: async (data: Partial<Task>, files?: File[], filesToDetach?: number[]) => {
      const { createTask } = await import('../../services/mutations');
      return await createTask(data, files, filesToDetach);
    },
    update: async (id: number, data: Partial<Task>, files?: File[], filesToDetach?: number[]) => {
      const { updateTask } = await import('../../services/mutations');
      return await updateTask(id, data, files, filesToDetach);
    },
    getViewFileUrl: (entityId: number, fileId: number) => `/crm-tasks/tasks/${entityId}/files/${fileId}`,
  },

  // Unified Field Configuration
  fields: [
    {
      name: 'taskDescription',
      label: 'Task Description',
      type: 'textarea',
      required: true,
      placeholder: 'Enter task description',
      rows: 3,
      validation: {
        required: true,
        minLength: 3,
        maxLength: 2000,
      },
    },
    {
      name: 'clientId',
      label: 'Client',
      type: 'select',
      placeholder: 'Select a client (optional)',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/crm-clients', // API endpoint to fetch all client options
      labelField: 'clientName', // Field name in API response to use as label
    },
    {
      name: 'taskAssignedFrom',
      label: 'Assigned From',
      type: 'select',
      required: true,
      placeholder: 'Select a user',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/users', // API endpoint to fetch all user options
      labelField: 'userDisplayName', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'taskAssignedTo',
      label: 'Asgn',
      type: 'select',
      required: true,
      placeholder: 'Select a user',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/users', // API endpoint to fetch all user options
      labelField: 'userDisplayName', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'projectId',
      label: 'Project',
      type: 'select',
      placeholder: 'Select a project (optional)',
      options: [], // Will be populated dynamically in Form.tsx
    },
    {
      name: 'supportTicketId',
      label: 'Support Ticket',
      type: 'select',
      placeholder: 'Select a support ticket (optional)',
      options: [], // Will be populated dynamically in Form.tsx
    },
    {
      name: 'taskCategoryId',
      label: 'Task Category',
      type: 'select',
      required: true,
      placeholder: 'Select a category',
      options: [], // Will be populated dynamically in Form.tsx
      validation: {
        required: true,
      },
    },
    {
      name: 'taskPriority',
      label: 'Priority',
      type: 'select',
      placeholder: 'Select a priority (optional)',
      options: [], // Will be populated dynamically in Form.tsx
    },
    {
      name: 'taskStatus',
      label: 'Task Status',
      type: 'select',
      required: true,
      placeholder: 'Select a status',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/p-crm-task-status', // API endpoint to fetch all task status options
      labelField: 'ts_descr', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'taskDate',
      label: 'Task Date',
      type: 'date',
      required: true,
      placeholder: 'Select task date',
      validation: {
        required: true,
      },
    },
    {
      name: 'taskEstimatedDate',
      label: 'Estimated Date',
      type: 'date',
      placeholder: 'Select estimated date',
    },
    {
      name: 'taskStartDate',
      label: 'Start Date',
      type: 'datetime',
      placeholder: 'Select start date and time',
      calculateStartFrom: {
        endDateTimeField: 'taskEndDate',
        durationMinutesField: 'taskSpentMin',
      },
    },
    {
      name: 'taskEndDate',
      label: 'End Date',
      type: 'datetime',
      placeholder: 'Select end date and time',
      calculateEndFrom: {
        startDateTimeField: 'taskStartDate',
        durationMinutesField: 'taskSpentMin',
      },
    },
    {
      name: 'taskSpentMin',
      label: 'Time Spent (minutes)',
      type: 'number',
      placeholder: 'Enter time spent in minutes',
      validation: {
        min: 0,
      },
    },
    {
      name: 'taskInvoicedMin',
      label: 'Time Invoiced (minutes)',
      type: 'number',
      placeholder: 'Enter time invoiced in minutes',
      validation: {
        min: 0,
      },
    },
    {
      name: 'taskNotes',
      label: 'Task Notes',
      type: 'textarea',
      placeholder: 'Enter task notes',
      rows: 4,
      validation: {
        maxLength: 2000,
      },
    },
    {
      name: 'taskCommonNote',
      label: 'Common Note',
      type: 'textarea',
      placeholder: 'Enter common note',
      rows: 4,
      validation: {
        maxLength: 2000,
      },
    },
  ] as FieldConfig[],

  // Form Layout Configuration (complaint-style: gridCols + fieldSpans per section)
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Basic Information',
        icon: '📄',
        gridCols: 'md:grid-cols-12',
        fields: ['taskDescription', 'clientId'],
        fieldSpans: {
          taskDescription: 'md:col-span-12',
          clientId: 'md:col-span-12',
        },
        collapsible: true,
      },
      {
        id: 'assignment',
        title: 'Assignment',
        icon: '👥',
        gridCols: 'md:grid-cols-2',
        fields: ['taskAssignedFrom', 'taskAssignedTo'],
        collapsible: true,
      },
      {
        id: 'details',
        title: 'Task Details',
        icon: '📋',
        gridCols: 'md:grid-cols-2',
        fields: ['taskCategoryId', 'taskPriority', 'taskStatus'],
        collapsible: true,
      },
      {
        id: 'dates',
        title: 'Dates & Timeline',
        icon: '📅',
        gridCols: 'md:grid-cols-2',
        fields: ['taskDate', 'taskEstimatedDate', 'taskStartDate', 'taskEndDate'],
        collapsible: true,
      },
      {
        id: 'time',
        title: 'Time Tracking',
        icon: '⏱️',
        gridCols: 'md:grid-cols-2',
        fields: ['taskSpentMin', 'taskInvoicedMin'],
        collapsible: true,
      },
      {
        id: 'additional',
        title: 'Additional Information',
        icon: '📝',
        gridCols: 'md:grid-cols-12',
        fields: ['projectId', 'supportTicketId', 'taskNotes', 'taskCommonNote'],
        fieldSpans: {
          projectId: 'md:col-span-6',
          supportTicketId: 'md:col-span-6',
          taskNotes: 'md:col-span-6',
          taskCommonNote: 'md:col-span-6',
        },
        collapsible: true,
      },
    ],
  } as FormLayout,
};

// Helper function to get columns
export const getColumns = (
  formatDate: (dateString?: string) => string,
  colors?: ThemeColors
): Column<Task>[] => {
  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '-';
    return formatDateTimeGlobal(dateString) || '-';
  };

  const textMuted = colors?.textMuted || '';
  
  return [
  {
    key: 'taskDescription',
    header: 'Description',
    sortable: true,
    render: (value, row) => {
      const projectLabel = row.project?.projectName || (row.projectId ? `Project #${row.projectId}` : null);
      const supportTicketLabel =
        row.supportTicket?.title ||
        row.supportTicket?.reference ||
        (row.supportTicketId ? `Ticket #${row.supportTicketId}` : null);

      const metaItems: Array<{
        key: string;
        label: string;
        icon: React.ReactNode;
      }> = [];

      if (projectLabel) {
        metaItems.push({
          key: 'project',
          label: projectLabel,
          icon: (
            <svg className="h-3 w-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h5l2 2h11v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
            </svg>
          ),
        });
      }

      if (supportTicketLabel) {
        metaItems.push({
          key: 'support-ticket',
          label: supportTicketLabel,
          icon: (
            <svg className="h-3 w-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h3m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          ),
        });
      }

      const clientLine = row.client?.clientName || (row.clientId ? `Client #${row.clientId}` : null);
      const hasAttachments = (row.FileAttachment?.length ?? 0) > 0;
      const attachmentCount = row.FileAttachment?.length ?? 0;

      return (
        <div className="min-w-0">
          <span className="inline-flex items-center gap-1.5 min-w-0">
            <span className="min-w-0 flex-1">
              <TruncatedText
                text={value || ''}
                maxLength={50}
                showTooltip={true}
              />
            </span>
            {hasAttachments && (
              <span
                title={`${attachmentCount} file${attachmentCount !== 1 ? 's' : ''} attached`}
                className="shrink-0 inline-flex text-gray-500 dark:text-gray-400"
                aria-hidden
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
              </span>
            )}
          </span>
          {clientLine && (
            <div className={`mt-0.5 text-xs ${textMuted}`}>
              <span className="truncate">{clientLine}</span>
            </div>
          )}
          {metaItems.length > 0 && (
            <div className={`mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs ${textMuted}`}>
              {metaItems.map((item) => (
                <span key={item.key} className="inline-flex min-w-0 items-center gap-1">
                  {item.icon}
                  <span className="truncate">{item.label}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      );
    },
  },
  {
    key: 'taskCategoryDescr',
    header: 'Category',
    sortable: true,
    render: (_value, row) => row.taskCategoryDescr?.tc_descr || '-',
  },
  {
    key: 'taskAssignedToUser',
    header: 'Asgn',
    sortable: true,
    render: (value, row) => {
      const user = row.taskAssignedToUser;
      const displayName = user?.userDisplayName || user?.userFirstName || (user?.id ? `User #${user.id}` : null);
      return (
        displayName || '-'
      );
    },
  },
  {
    key: 'taskDate',
    header: 'Date',
    sortable: true,
    render: (value) => formatDate(value),
  },
  {
    key: 'taskStatusDescr',
    header: 'Status',
    sortable: true,
    render: (value, row) => {
      const status = row.taskStatusDescr?.ts_descr || '';
      if (!status) return <span className={`text-sm ${textMuted}`}>-</span>;

      return renderTaskStatusBadge(status, row.taskStatusDescr?.ts_color);
    },
  },
  {
    key: 'taskTags',
    header: 'Tags',
    sortable: false,
    render: (value, row) => {
      const tags = row.taskTags || [];
      if (!tags || tags.length === 0) return <span className={`text-sm ${textMuted}`}>-</span>;
      
      return (
        <div className="flex flex-wrap gap-1">
          {tags.map((tag: { name?: string; color?: string }, index: number) => (
            <ColoredBadge key={index} label={tag.name} color={tag.color || '#ccc'} />
          ))}
        </div>
      );
    },
  },
  {
    key: 'taskStartDate',
    header: 'Start Date',
    sortable: true,
    render: (value) => formatDateTime(value),
  },
  {
    key: 'taskEndDate',
    header: 'End Date',
    sortable: true,
    render: (value) => formatDateTime(value),
  },
  {
    key: 'taskSpentMin',
    header: 'Spent',
    sortable: true,
    render: (value) => (
      value ? `${value} mins` : '-'
    ),
  },
  {
    key: 'taskInvoicedMin',
    header: 'Invoiced',
    sortable: true,
    render: (value) => (
      value ? `${value} mins` : '-'
    ),
  },
];
};

// Helper function to get detail sections
export const getDetailSections = (
  item: Task,
  formatDate: (dateString?: string) => string,
  formatDateTime: (dateString?: string) => string,
  colors?: ThemeColors
) => {
  const textColor = colors?.text ;
  
  return [
    {
      title: 'Basic Information',
      fields: [
        {
          label: 'Description',
          value: item.taskDescription ? (
            <TruncatedText
              text={item.taskDescription}
              maxLength={200}
              className={`text-sm ${textColor}`}
              showTooltip={true}
            />
          ) : '-',
        },
        {
          label: 'Client',
          value: item.client?.clientName || '-',
        },
        {
          label: 'Assigned From',
          value: item.taskAssignedFromUser?.userDisplayName || item.taskAssignedFromUser?.userFirstName || (item.taskAssignedFromUser?.id ? `User #${item.taskAssignedFromUser.id}` : '-'),
        },
        {
          label: 'Asgn',
          value: item.taskAssignedToUser?.userDisplayName || item.taskAssignedToUser?.userFirstName || (item.taskAssignedToUser?.id ? `User #${item.taskAssignedToUser.id}` : '-'),
        },
        {
          label: 'Task Date',
          value: formatDate(item.taskDate),
        },
      ],
    },
    {
      title: 'Task Details',
      fields: [
        {
          label: 'Category',
          value: item.taskCategoryDescr?.tc_descr || '-',
        },
        {
          label: 'Type',
          value: item.taskTypeDescr?.tt_descr || '-',
        },
        {
          label: 'Priority',
          value: item.taskPriorityDescr?.p_descr || '-',
        },
        {
          label: 'Status',
          value: renderTaskStatusBadge(item.taskStatusDescr?.ts_descr, item.taskStatusDescr?.ts_color) || '-',
        },
      ],
    },
    {
      title: 'Dates & Timeline',
      fields: [
        {
          label: 'Estimated Date',
          value: formatDateTime(item.taskEstimatedDate),
        },
        {
          label: 'Start Date',
          value: formatDateTime(item.taskStartDate),
        },
        {
          label: 'End Date',
          value: formatDateTime(item.taskEndDate),
        },
      ],
    },
    {
      title: 'Time Tracking',
      fields: [
        {
          label: 'Time Spent',
          value: item.taskSpentMin ? `${item.taskSpentMin} minutes` : '-',
        },
        {
          label: 'Time Invoiced',
          value: item.taskInvoicedMin ? `${item.taskInvoicedMin} minutes` : '-',
        },
      ],
    },
    {
      title: 'Additional Information',
      fields: [
        {
          label: 'Notes',
          value: item.taskNotes || '-',
        },
        {
          label: 'Common Note',
          value: item.taskCommonNote || '-',
        },
        {
          label: 'Created At',
          value: formatDateTime(item.taskCreatedAt),
        },
        {
          label: 'Updated At',
          value: formatDateTime(item.taskUpdatedAt),
        },
      ],
    },
    {
      title: 'Attachments',
      icon: '📎',
      fields: [
        {
          label: '',
          value: React.createElement(TaskAttachmentsSection, {
            taskId: item.taskId,
            files: item.FileAttachment || [],
          }),
        },
      ],
    },
    {
      title: 'Conversations',
      icon: '💬',
      fields: [
        {
          label: '',
          value: React.createElement(LinkedConversationsSection, { taskId: item.taskId }),
        },
      ],
    },
    {
      title: 'Activity Log',
      icon: '📋',
      fields: [
        {
          label: '',
          value: React.createElement(ActivityLogSection, { entityType: 'Task', entityId: item.taskId }),
        },
      ],
    },
  ];
};

// Helper function to get status badges (themeColors required for light/dark support)
export const getStatusBadges = (item: Task, themeColors?: ThemeColors) => {
  const status = item.taskStatusDescr?.ts_descr || '';
  if (!status) return null;
  void themeColors;
  return renderTaskStatusBadge(status, item.taskStatusDescr?.ts_color, 'px-3 py-1 text-sm font-semibold');
};

// Helper function to render toolbar icon (same icon style as Notes module for view toggle)
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  const iconClass = 'w-5 h-5';
  const svgProps = { className: iconClass, fill: 'none' as const, stroke: 'currentColor' as const, viewBox: '0 0 24 24' };
  switch (iconName) {
    case 'download':
      return (
        <svg {...svgProps}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    case 'add':
      return (
        <svg {...svgProps}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    case 'list':
      return (
        <svg {...svgProps}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      );
    case 'calendar':
      return (
        <svg {...svgProps}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    case 'kanban':
      return (
        <svg {...svgProps}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
        </svg>
      );
    case 'add-support-task':
      return (
        <svg {...svgProps}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
        </svg>
      );
    default:
      return null;
  }
};

// Helper function to render table action buttons - matching support-ticket styling
// Notify client button only when onNotifyClient is provided (task notifications enabled) and task has a client
export const getTableActions = (
  item: Task,
  onDelete: (id: number) => void,
  _colors?: unknown,
  onNotifyClient?: (taskId: number) => void,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.taskId)}
      editTo={moduleConfig.module.route.edit(item.taskId)}
      onDelete={() => onDelete(item.taskId)}
      onNotify={item.clientId && onNotifyClient ? () => onNotifyClient(item.taskId) : undefined}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};


export const getInitialFormData = (): Partial<Task> => {
  return {
    taskDescription: '',
    clientId: undefined,
    taskAssignedFrom: undefined,
    taskAssignedTo: undefined,
    projectId: undefined,
    supportTicketId: undefined,
    taskCategoryId: undefined,
    taskTypeId: undefined,
    taskPriority: undefined,
    taskStatus: undefined,
    taskDate: undefined,
    taskEstimatedDate: undefined,
    taskStartDate: undefined,
    taskEndDate: undefined,
    taskSpentMin: undefined,
    taskInvoicedMin: undefined,
    taskNotes: '',
    taskCommonNote: '',
    taskTags: undefined,
  };
};

// Helper function to map item data to form data
export const mapItemToFormData = (item: Task): Partial<Task> => {
  return {
    taskDescription: item.taskDescription || '',
    clientId: item.clientId || undefined,
    taskAssignedFrom: item.taskAssignedFrom || undefined,
    taskAssignedTo: item.taskAssignedTo || undefined,
    projectId: item.projectId || undefined,
    supportTicketId: item.supportTicketId || undefined,
    taskCategoryId: item.taskCategoryId || undefined,
    taskTypeId: item.taskTypeId || undefined,
    taskPriority: item.taskPriority || undefined,
    taskStatus: item.taskStatus || undefined,
    taskDate: item.taskDate || undefined,
    taskEstimatedDate: item.taskEstimatedDate || undefined,
    taskStartDate: item.taskStartDate || undefined,
    taskEndDate: item.taskEndDate || undefined,
    taskSpentMin: item.taskSpentMin || undefined,
    taskInvoicedMin: item.taskInvoicedMin || undefined,
    taskNotes: item.taskNotes || '',
    taskCommonNote: item.taskCommonNote || '',
    taskTags: item.taskTags || undefined,
  };
};

export type ModuleConfig = typeof moduleConfig;

